import { useEffect, useRef, useState } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Upload, X, GripVertical, Image as ImageIcon, Star, Repeat } from "lucide-react";
import { api } from "@/lib/api";
import { formatApiError } from "@/context/AuthContext";
import { normalizeImages } from "@/lib/productImages";

const MAX_IMAGES = 10;
const MIN_RECOMMENDED = 5;
const MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED = ["image/jpeg", "image/png", "image/webp", "image/jpg"];

function Thumb({ img, index, onRemove, onPromote, onReplace, onAltChange, isHero }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: img.url });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const fileRef = useRef(null);

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`image-thumb-${index}`}
      className={`relative bg-[#0a0316] border transition-colors group ${
        isHero ? "border-[#ff1493]" : "border-[#2a1a3a] hover:border-[#9d4cdd]"
      }`}
    >
      <div className="aspect-[4/5] relative">
        <img src={img.url} alt={img.alt || ""} className="absolute inset-0 w-full h-full object-cover" draggable={false} />

        {isHero && (
          <div className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#ff1493] text-black font-mono text-[9px] tracking-[0.2em] uppercase">
            <Star size={9} strokeWidth={2} fill="currentColor" /> Hero
          </div>
        )}

        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/55 transition-colors flex items-center justify-center gap-1.5 opacity-0 group-hover:opacity-100">
          {!isHero && (
            <button
              type="button"
              data-testid={`image-promote-${index}`}
              onClick={(e) => {
                e.preventDefault();
                onPromote();
              }}
              title="Set as hero"
              className="p-1.5 bg-[#ff1493] text-black"
              aria-label="Set as hero"
            >
              <Star size={12} strokeWidth={2} />
            </button>
          )}
          <button
            type="button"
            data-testid={`image-replace-${index}`}
            onClick={(e) => {
              e.preventDefault();
              fileRef.current?.click();
            }}
            title="Replace image"
            className="p-1.5 bg-[#9d4cdd] text-white"
            aria-label="Replace image"
          >
            <Repeat size={12} strokeWidth={1.75} />
          </button>
          <button
            type="button"
            data-testid={`image-remove-${index}`}
            onClick={(e) => {
              e.preventDefault();
              onRemove();
            }}
            title="Remove"
            className="p-1.5 bg-[#1a0a2a] text-white border border-[#ff3b30]/60 hover:bg-[#ff3b30] hover:text-black"
            aria-label="Remove image"
          >
            <X size={12} strokeWidth={2} />
          </button>
        </div>

        <button
          type="button"
          {...attributes}
          {...listeners}
          aria-label="Drag to reorder"
          className="absolute top-1.5 right-1.5 p-1 bg-black/70 text-white opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
        >
          <GripVertical size={11} strokeWidth={1.5} />
        </button>

        <input
          ref={fileRef}
          type="file"
          className="hidden"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            if (f) onReplace(f);
          }}
        />
      </div>

      <input
        data-testid={`image-alt-${index}`}
        type="text"
        value={img.alt || ""}
        onChange={(e) => onAltChange(e.target.value)}
        placeholder="alt text (for accessibility / SEO)"
        className="w-full bg-[#06000e] border-t border-[#1a0a2a] px-2 py-1.5 font-mono text-[10px] tracking-[0.05em] text-white placeholder:text-[#7a6a90] focus:outline-none focus:border-[#ff1493]"
      />
    </div>
  );
}

export default function ImageManager({ images = [], onChange }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const inputRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Normalize on every render so callers can pass any legacy shape
  const list = normalizeImages(images);

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(""), 2200);
    return () => clearTimeout(t);
  }, [success]);

  function handleDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = list.findIndex((i) => i.url === active.id);
    const newIndex = list.findIndex((i) => i.url === over.id);
    onChange(arrayMove(list, oldIndex, newIndex));
  }

  async function uploadOne(file) {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/admin/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data.url;
  }

  function validateFile(file) {
    if (!ALLOWED.includes(file.type)) {
      return `Unsupported type: ${file.name}. JPG/PNG/WEBP only.`;
    }
    if (file.size > MAX_BYTES) {
      return `${file.name} exceeds 10 MB.`;
    }
    return null;
  }

  async function handleFiles(fileList) {
    setError("");
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (list.length + files.length > MAX_IMAGES) {
      setError(`Max ${MAX_IMAGES} images per product.`);
      return;
    }
    setUploading(true);
    try {
      const newItems = [...list];
      for (const file of files) {
        const v = validateFile(file);
        if (v) {
          setError(v);
          continue;
        }
        const url = await uploadOne(file);
        newItems.push({ url, alt: "" });
      }
      onChange(newItems);
      setSuccess(`${files.length} image${files.length === 1 ? "" : "s"} uploaded.`);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Upload failed.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  async function handleReplace(index, file) {
    setError("");
    const v = validateFile(file);
    if (v) {
      setError(v);
      return;
    }
    setUploading(true);
    try {
      const url = await uploadOne(file);
      const next = [...list];
      next[index] = { url, alt: next[index]?.alt || "" };
      onChange(next);
      setSuccess("Image replaced.");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Replace failed.");
    } finally {
      setUploading(false);
    }
  }

  function removeAt(index) {
    onChange(list.filter((_, i) => i !== index));
  }

  function promoteAt(index) {
    if (index === 0) return;
    const next = [...list];
    const [item] = next.splice(index, 1);
    next.unshift(item);
    onChange(next);
    setSuccess("Hero updated.");
  }

  function altAt(index, value) {
    const next = [...list];
    next[index] = { ...next[index], alt: value };
    onChange(next);
  }

  const hero = list[0];

  return (
    <div data-testid="image-manager">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[#ff1493]">
          // Images ({list.length}/{MAX_IMAGES}) · Recommended {MIN_RECOMMENDED}–8
        </div>
        <label
          className={`btn-ghost cursor-pointer ${uploading ? "opacity-60 pointer-events-none" : ""}`}
          data-testid="image-upload-label"
        >
          <Upload size={12} strokeWidth={1.5} />
          {uploading ? "Uploading…" : "Upload"}
          <input
            ref={inputRef}
            data-testid="image-upload-input"
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />
        </label>
      </div>

      {error && (
        <p data-testid="image-manager-error" className="mb-3 font-mono text-[10px] tracking-[0.2em] uppercase text-[#ff3b30]">
          // {error}
        </p>
      )}
      {success && (
        <p data-testid="image-manager-success" className="mb-3 font-mono text-[10px] tracking-[0.2em] uppercase text-[#00ff41]">
          // {success}
        </p>
      )}

      {/* Hero preview */}
      <div className="mb-5 grid md:grid-cols-[260px_1fr] gap-5 items-start" data-testid="image-manager-hero">
        <div className="relative bg-[#0a0316] border-2 border-[#ff1493] aspect-[4/5]">
          {hero ? (
            <img src={hero.url} alt={hero.alt || ""} className="absolute inset-0 w-full h-full object-cover" draggable={false} />
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-[#7a6a90]">
              <ImageIcon size={22} strokeWidth={1.2} />
              <span className="font-mono text-[10px] tracking-[0.22em] uppercase">No hero</span>
            </div>
          )}
          <div className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#ff1493] text-black font-mono text-[9px] tracking-[0.2em] uppercase">
            <Star size={9} strokeWidth={2} fill="currentColor" /> Hero
          </div>
        </div>
        <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#b4a3c7] leading-relaxed">
          <div className="text-[#ff1493] mb-2">// Hero image</div>
          <p>
            The first image is the hero. It powers the cover on Shop and Drop 001 cards, and is the opening frame on the product detail page.
          </p>
          <p className="mt-3 text-[#7a6a90]">
            Hover any gallery thumbnail to set it as hero, replace its file, or remove it. Drag to reorder. Alt text fields below each thumbnail are saved to MongoDB with the product.
          </p>
        </div>
      </div>

      {/* Gallery thumbnails */}
      {list.length === 0 ? (
        <div className="border border-dashed border-[#2a1a3a] aspect-[4/5] max-w-[200px] flex flex-col items-center justify-center gap-2 text-[#7a6a90]">
          <ImageIcon size={20} strokeWidth={1.2} />
          <span className="font-mono text-[10px] tracking-[0.22em] uppercase">No images</span>
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={list.map((i) => i.url)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="image-grid">
              {list.map((img, idx) => (
                <Thumb
                  key={img.url}
                  img={img}
                  index={idx}
                  isHero={idx === 0}
                  onRemove={() => removeAt(idx)}
                  onPromote={() => promoteAt(idx)}
                  onReplace={(file) => handleReplace(idx, file)}
                  onAltChange={(v) => altAt(idx, v)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <p className="mt-3 font-mono text-[10px] tracking-[0.18em] uppercase text-[#7a6a90]">
        // First image = hero. Hover any thumb to promote / replace / remove. Drag to reorder. JPG / PNG / WEBP · 10 MB max.
      </p>
    </div>
  );
}
